'use client'

import { motion, type MotionValue } from 'motion/react'
import { ArrowDown } from 'lucide-react'
import { useAccountingSequence, useSequenceCaption } from '../hooks/use-accounting-sequence'
import { StoryPicture } from './story-picture'
import styles from '../styles/story.module.css'

const chapters = [
  { label: '01 — Birikim', title: ['Her şey bir', 'kayıtla başladı.'], copy: 'Bir satır. Bir hesap. Yıllar boyunca büyüyen bir bilgi.', frame: 0 },
  { label: '02 — Dönüşüm', title: ['Sayfalar değişti.', 'Özen aynı kaldı.'], copy: 'Defterlerde biriken bilgi, birbirine bağlı kayıtlara dönüştü.', frame: 60 },
  { label: '03 — Dijitalleşme', title: ['Kayıtlar dijital.', 'Bilgi bir bütün.'], copy: 'Belgeler, hesaplar ve dönemler. Birbirine bağlı bir çalışma alanı.', frame: 110 },
  { label: '04 — Yapay zekâ', title: ['Veriyi okur.', 'Anlamı bulur.'], copy: 'Yapay zekâ belgeleri okur, siz kontrol eder ve kayda dönüştürürsünüz.', frame: 179 },
]
const posterAlt = 'Aydınlık bir ortamda açık muhasebe defteri'

function SceneCaption({ progress, index, active }: { progress: MotionValue<number>; index: number; active: boolean }) {
  const animation = useSequenceCaption(progress, index)
  const chapter = chapters[index]
  return <motion.div className={styles.caption} style={animation} aria-hidden={!active}>
    <p className={styles.eyebrow}>{chapter.label}</p>
    {index === 0
      ? <h1>{chapter.title.map(line => <span key={line}>{line}</span>)}</h1>
      : <h2>{chapter.title.map(line => <span key={line}>{line}</span>)}</h2>}
    <p className={styles.description}>{chapter.copy}</p>
  </motion.div>
}

function StillStory() {
  return <div className={styles.stills}>
    {chapters.map((chapter, index) => <section key={chapter.label} className={styles.still}>
      <StoryPicture frame={chapter.frame} loading={index ? 'lazy' : 'eager'} />
      <div className={styles.stillCopy}>
        <p className={styles.eyebrow}>{chapter.label}</p>
        {index === 0 ? <h1>{chapter.title.join(' ')}</h1> : <h2>{chapter.title.join(' ')}</h2>}
        <p className={styles.description}>{chapter.copy}</p>
      </div>
    </section>)}
  </div>
}

export function CinematicStory() {
  const { sectionRef, canvasRef, progress, focusOpacity, reduced, ready, failed, loading, opening, prominentHint, loadingPercent, scene, toggleMotion, systemReduced } = useAccountingSequence()
  const staticMode = reduced || failed
  return <div className={styles.page} data-static={staticMode}>
    {loading && <div className={styles.loading}>
      <StoryPicture frame={0} alt={posterAlt} className={styles.loadingBackdrop} loading="eager" fetchPriority="high" />
      <div className={styles.loadingContent}>
        <span className={styles.loadingBrand}>Muhasebe AI</span>
        <p role="status">Görseller yükleniyor.</p>
        <span className={styles.loadingDescription}>Akıcı bir deneyim için kısa bir süre bekleyin.</span>
        <div className={styles.loadingTrack} role="progressbar" aria-label="Görseller yükleniyor" aria-valuemin={0} aria-valuemax={100} aria-valuenow={loadingPercent}>
          <div style={{ transform: `scaleX(${loadingPercent / 100})` }} />
        </div>
        <span className={styles.loadingPercent} aria-hidden="true">%{loadingPercent}</span>
        <a href="#features" className={styles.loadingSkip}>Hikâyeyi geç</a>
      </div>
    </div>}
    <a href="#features" className={styles.skip}>Hikâyeyi geç</a>
    <section id="accounting-sequence" ref={sectionRef} className={styles.sequence} data-opening-active={opening || prominentHint} aria-label="Muhasebenin dönüşümü">
      <div className={styles.stage}>
        <StoryPicture frame={0} alt={posterAlt} className={styles.poster} fetchPriority="high" />
        <canvas ref={canvasRef} className={styles.canvas} data-ready={ready} aria-hidden="true" />
        <div className={styles.scrim} aria-hidden="true" />
        <motion.div className={styles.focusScrim} style={{ opacity: focusOpacity }} aria-hidden="true" />
        {chapters.map((chapter, index) => <SceneCaption key={chapter.title.join(" ")} index={index} active={scene === index} progress={progress} />)}
        {prominentHint && <div className={styles.prominentHint}><ArrowDown size={22} strokeWidth={1.5} /><span>Devam etmek için kaydırın</span></div>}
        <div className={styles.bottom}>
          <span className={styles.scrollHint} data-opening={loading || opening || prominentHint} aria-hidden={loading || opening || prominentHint}><ArrowDown size={17} strokeWidth={1.5} /> Devam etmek için kaydırın</span>
          <div className={styles.timeline} aria-hidden="true">
            <div className={styles.timelineLabels}><span>Defterden</span><span>Yapay zekâya</span></div>
            <div className={styles.track}><motion.div style={{ scaleX: progress }} /></div>
          </div>
          <span className={styles.chapterNumber} aria-hidden="true">0{scene + 1}<span> / 04</span></span>
        </div>
      </div>
    </section>

    {staticMode && <StillStory />}
    <noscript><StillStory /></noscript>
    <div className={styles.motionControl}>{!systemReduced && <button type="button" onClick={toggleMotion} aria-pressed={reduced}>{reduced ? 'Hareketli görünüm' : 'Hareketi azalt'}</button>}</div>
  </div>
}
